import { createTheme } from '@mui/material/styles';

// Design tokens pulled from the Figma home-page export.
// Colors/fonts that repeat across sections live here; one-off decorative
// positioning (bubbles, mascot images, exact px offsets) stays in the
// components that use them — that's layout, not a reusable token.

export const colors = {
  gold: '#EBB111', // primary accent — big headings, stat numbers, CTA buttons
  purple: '#8F4CFA', // "HACKATHON" nav wordmark
  navy: '#020F5D', // deep navy used throughout the background gradients
  navSurface: '#19142A', // navigation bar background
  footerSurface: '#1A1A24', // footer background
  footerBorder: 'rgba(139, 92, 246, 0.2)',
  white: '#FFFFFF',
  offWhite: '#EFEFEF', // nav link text
  mutedGray: '#9CA3AF', // footer copyright text
  sponsorHeading: '#333333', // "Platinum/Gold/Silver Sponsors" labels
  sponsorText: '#666666', // sponsor placeholder logo text
  sponsorPlaceholderBg: '#E0E0E0',
  sponsorPlaceholderBorder: '#999999',
} as const;

export const fonts = {
  display: "'Pixelify Sans', sans-serif", // big pixel-art section headings ("SanD Hacks", "Our Impact")
  heading: "'Space Grotesk', sans-serif", // hero subtitle + nav wordmark
  mono: "'Intel One Mono', monospace", // dates, stat numbers, nav links, buttons — NOT a Google Font, see note below
  body: "'Space Mono', monospace", // paragraph copy ("What is CSES Hackathon?")
  ui: "'Roboto', sans-serif", // sponsor section labels
  fine: "'Inter', sans-serif", // small print (footer copyright)
} as const;

// Reused background gradients from the Figma frames
export const gradients = {
  page: 'linear-gradient(180deg, #2B2F3E 1.7%, #3689DD 5.5%, #3689DD 21%, #7B3196 33.5%, #7B3196 100%)',
  hero: 'radial-gradient(50% 50% at 50% 50%, #ADE1FF 31.73%, #020F5D 48.08%, rgba(255, 255, 255, 0) 100%)',
  schedule: 'radial-gradient(50% 50% at 50% 50%, #C9AE7A 0%, #A87F2E 20.19%, #020F5D 93.27%)',
  impact: 'radial-gradient(50% 50% at 50% 50%, rgba(235, 177, 17, 0.55) 0%, rgba(2, 15, 93, 0.5) 100%)',
} as const;

declare module '@mui/material/styles' {
  interface Theme {
    gradients: typeof gradients;
  }
  interface ThemeOptions {
    gradients?: typeof gradients;
  }
}

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: colors.gold, contrastText: colors.white },
    secondary: { main: colors.purple, contrastText: colors.white },
    background: { default: colors.navy, paper: colors.navSurface },
    text: { primary: colors.white, secondary: colors.mutedGray },
  },
  typography: {
    fontFamily: fonts.mono,
    h1: {
      fontFamily: fonts.display,
      fontWeight: 700,
      fontSize: '200px',
      lineHeight: '200px',
      color: colors.gold,
      textShadow: '0px 4px 4px rgba(0, 0, 0, 0.5)',
    },
    h2: {
      fontFamily: fonts.display,
      fontWeight: 700,
      fontSize: '100px',
      lineHeight: '64px',
      color: colors.gold,
      textShadow: '0px 4px 4px rgba(0, 0, 0, 0.5)',
    },
    h3: {
      fontFamily: fonts.ui,
      fontWeight: 700,
      fontSize: '24px',
      lineHeight: '38px',
      color: colors.sponsorHeading,
    },
    subtitle1: {
      fontFamily: fonts.heading,
      fontWeight: 500,
      fontSize: '40px',
      lineHeight: '32px',
      color: colors.white,
    },
    body1: {
      fontFamily: fonts.body,
      fontWeight: 700,
      fontSize: '40px',
      lineHeight: '50px',
      color: colors.white,
    },
    button: {
      fontFamily: fonts.mono,
      fontWeight: 700,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          boxShadow: '1px 1px 7px #FFFFFF',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        // CssBaseline otherwise paints palette.background.default as an
        // opaque body background, hiding the gradient set on <html>.
        body: {
          backgroundColor: 'transparent',
        },
      },
    },
  },
  gradients,
});

export default theme;
